import { useState } from 'react';
import {
  Box, Button, Divider, FormControl, InputLabel, MenuItem, Select, Stack, Typography
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { Link } from 'react-router';
import { subMonths } from 'date-fns';
import { useAppSelector } from '../hooks';
import ResultsTable from './ResultsTable';
import type { PeriodType } from '../api/types';

interface Shortcut {
  label: string;
  type: PeriodType;
  value: () => Date;
}

const SHORTCUTS: Shortcut[] = [
  { label: 'Ten miesiąc', type: 'month', value: () => new Date() },
  { label: 'Poprzedni miesiąc', type: 'month', value: () => subMonths(new Date(), 1) },
  { label: 'Ten rok', type: 'year', value: () => new Date() },
  { label: 'Wszystko', type: 'all', value: () => new Date() }
];

export default function LoggedInApp() {
  const userName = useAppSelector((s) => s.auth.name);
  const isAdmin = useAppSelector((s) => s.auth.isAdmin);
  const [type, setType] = useState<PeriodType>('month');
  const [value, setValue] = useState<Date>(new Date());

  return (
    <div className="loggedInApp">
      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        flexWrap="wrap"
        alignItems="baseline"
        justifyContent="space-between"
        sx={{ mb: 1 }}
      >
        <Typography variant="h5">Wyniki</Typography>
        {isAdmin && (
          <Button component={Link} to="/admin/points/add" size="small" variant="contained">
            Dodaj punkty
          </Button>
        )}
      </Stack>

      <Box className="resultsSelect" sx={{ mb: 3 }}>
        {/* useFlexGap so the row actually wraps instead of overflowing on a phone. */}
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
          {SHORTCUTS.map((shortcut) => (
            <Button
              key={shortcut.label}
              size="small"
              variant={type === shortcut.type ? 'contained' : 'outlined'}
              onClick={() => { setType(shortcut.type); setValue(shortcut.value()); }}
            >
              {shortcut.label}
            </Button>
          ))}
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} useFlexGap>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="period-type">Okres</InputLabel>
            <Select
              labelId="period-type"
              label="Okres"
              value={type}
              onChange={(event) => setType(event.target.value as PeriodType)}
            >
              <MenuItem value="month">Miesiąc</MenuItem>
              <MenuItem value="year">Rok</MenuItem>
              <MenuItem value="all">Wszystko</MenuItem>
            </Select>
          </FormControl>
          <DatePicker
            label="Wybierz datę"
            value={value}
            onChange={(next) => { if (next) { setValue(next); } }}
            slotProps={{ textField: { size: 'small' } }}
            disabled={type === 'all'}
          />
        </Stack>
      </Box>

      <div className="resultsTable">
        <ResultsTable periodType={type} periodValue={value.valueOf()} />
      </div>

      <Divider sx={{ mt: 3 }} />
      <Stack
        className="loggedInBar"
        direction="row"
        spacing={1}
        useFlexGap
        flexWrap="wrap"
        alignItems="center"
        sx={{ mt: 2 }}
      >
        <Typography variant="body2" color="text.secondary">
          Jesteś zalogowany jako: {userName}
        </Typography>
        {isAdmin && (
          <Button component={Link} to="/admin" size="small" variant="text">Panel admina</Button>
        )}
        <Button component={Link} to="/logout" size="small" variant="text">Wyloguj</Button>
      </Stack>
    </div>
  );
}
